// frontend/src/billing/PaymentCheckout.js
// ══════════════════════════════════════════════════════════════════
// 🔥 VERSIÓN CORREGIDA v3 - Soporta DOS flujos:
//    1. Nuevo registro (con registrationToken)
//    2. Admin logueado (con JWT token de localStorage)
// ══════════════════════════════════════════════════════════════════
// ✅ FIX v1.8.9.4: Agregado userId en flujos logged_in (trial y paypal)
// ══════════════════════════════════════════════════════════════════

import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { API_ENDPOINTS } from '../config/api';
import './PaymentCheckout.css';

export default function PaymentCheckout() {
  const { state } = useLocation();
  const navigate = useNavigate();
  const paypalRef = useRef();
  
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [sdkReady, setSdkReady] = useState(false);

  // ══════════════════════════════════════════════════════════════════
  // ✅ DETERMINAR TIPO DE ACCESO: 'registration' o 'logged_in'
  // ══════════════════════════════════════════════════════════════════
  const [accessType, setAccessType] = useState('loading');
  const [authData, setAuthData] = useState({
    userId: null,
    condominiumId: null,
    registrationToken: null,  // Solo para registro nuevo
    jwtToken: null            // Solo para usuarios logueados
  });

  const plan = state?.plan || null;

  // ══════════════════════════════════════════════════════════════════
  // 🔧 FUNCIÓN AUXILIAR: Mapear plan.id al formato del backend
  // ══════════════════════════════════════════════════════════════════
  const getPlanType = (planId) => {
    const mapping = {
      'trial': 'monthly',
      'monthly': 'monthly',
      'mensual': 'monthly',
      'biannual': 'biannual',
      'semestral': 'biannual',
      'anual': 'annual',
      'annual': 'annual'
    };
    
    const planType = mapping[planId?.toLowerCase()] || 'monthly';
    console.log(`📋 Plan ID recibido: "${planId}" → Plan Type: "${planType}"`);
    return planType;
  };

  // ══════════════════════════════════════════════════════════════════
  // ✅ VALIDACIÓN DE ACCESO - SOPORTA AMBOS FLUJOS
  // ══════════════════════════════════════════════════════════════════
  useEffect(() => {
    console.log('═══════════════════════════════════════════════');
    console.log('🔒 Validando acceso al Checkout...');
    console.log('═══════════════════════════════════════════════');
    console.log('📦 State recibido:', state);

    // ─────────────────────────────────────────────────────────────────
    // CASO 1: Flujo de NUEVO REGISTRO (tiene registrationToken)
    // ─────────────────────────────────────────────────────────────────
    if (state?.registrationToken) {
      console.log('✅ FLUJO: Nuevo registro con registrationToken');
      setAuthData({
        userId: state.userId,
        condominiumId: state.condominiumId,
        registrationToken: state.registrationToken,
        jwtToken: null
      });
      setAccessType('registration');
      return;
    }

    // ─────────────────────────────────────────────────────────────────
    // CASO 2: Flujo de ADMIN LOGUEADO (tiene JWT en localStorage)
    // ─────────────────────────────────────────────────────────────────
    const jwtToken = localStorage.getItem('token');
    const storedUser = localStorage.getItem('user');

    if (jwtToken && storedUser) {
      try {
        const user = JSON.parse(storedUser);
        console.log('✅ FLUJO: Admin logueado con JWT');
        console.log('👤 Usuario:', user.name);
        
        if (user.condominium_id) {
          setAuthData({
            userId: user.id,
            condominiumId: user.condominium_id,
            registrationToken: null,
            jwtToken: jwtToken
          });
          setAccessType('logged_in');
          return;
        } else {
          console.warn('⚠️ Usuario sin condominium_id');
        }
      } catch (error) {
        console.error('❌ Error parseando usuario:', error);
      }
    }

    // ─────────────────────────────────────────────────────────────────
    // CASO 3: Sin acceso válido
    // ─────────────────────────────────────────────────────────────────
    console.error('❌ Acceso no autorizado al checkout');
    setError('Sesión no válida. Redirigiendo...');
    setTimeout(() => {
      navigate('/planes', {
        replace: true,
        state: { error: 'Debes iniciar sesión o registrarte primero.' }
      });
    }, 2000);

  }, [state, navigate]);

  // ══════════════════════════════════════════════════════════════════
  // VALIDACIÓN DE PLAN Y CARGA DE PAYPAL SDK
  // ══════════════════════════════════════════════════════════════════
  useEffect(() => {
    // Esperar a que se determine el tipo de acceso
    if (accessType === 'loading') return;

    console.log('🟢 [CHECKOUT] Tipo de acceso:', accessType);
    console.log('📦 Datos de auth:', authData);

    // 1. Validar que existe plan
    if (!plan) {
      console.error('❌ No hay plan seleccionado');
      navigate('/planes');
      return;
    }

    // 2. Validar que existe condominiumId
    if (!authData.condominiumId) {
      console.error('❌ Falta condominiumId');
      setError('⚠️ Debes completar el registro primero. Redirigiendo...');
      setTimeout(() => navigate('/register'), 3000);
      return;
    }

    // 3. Si es TRIAL, NO cargar PayPal
    if (plan.isTrial) {
      console.log('🎁 Plan TRIAL detectado - No se carga PayPal');
      setLoading(false);
      return;
    }

    // 4. Si NO es trial, cargar PayPal SDK
    console.log('💳 Plan de pago detectado - Cargando PayPal...');
    
    const CLIENT_ID = process.env.REACT_APP_PAYPAL_CLIENT_ID;
    
    console.log('🔑 PayPal Client ID:', CLIENT_ID ? 'Configurado ✅' : 'NO CONFIGURADO ❌');

    if (!CLIENT_ID || CLIENT_ID === 'test' || CLIENT_ID === 'YOUR_PAYPAL_CLIENT_ID_HERE') {
      setError('⚠️ CONFIGURACIÓN PENDIENTE: Debes agregar tu PayPal Client ID real en frontend/.env');
      setLoading(false);
      return;
    }

    // Verificar si ya existe el script
    const existingScript = document.querySelector('script[src*="paypal.com/sdk/js"]');
    if (existingScript && window.paypal) {
      console.log('✅ PayPal SDK ya estaba cargado');
      setSdkReady(true);
      setLoading(false);
      return;
    }

    // Crear script
    const script = document.createElement('script');
    script.src = `https://www.paypal.com/sdk/js?client-id=${CLIENT_ID}&currency=USD`;
    script.async = true;
    
    const timeoutId = setTimeout(() => {
      if (!sdkReady) {
        console.error('❌ Timeout: PayPal SDK no cargó en 15 segundos');
        setError('PayPal está tardando mucho. Verifica tu conexión.');
        setLoading(false);
      }
    }, 15000);
    
    script.addEventListener('load', () => {
      clearTimeout(timeoutId);
      console.log('📦 Script de PayPal cargado');
      
      setTimeout(() => {
        if (window.paypal) {
          console.log('✅ window.paypal disponible y listo');
          setSdkReady(true);
          setLoading(false);
        } else {
          console.error('❌ window.paypal no se inicializó correctamente');
          setError('Error al inicializar PayPal. Recarga la página.');
          setLoading(false);
        }
      }, 500);
    });

    script.addEventListener('error', () => {
      clearTimeout(timeoutId);
      console.error('❌ Error al cargar PayPal SDK');
      setError('Error al cargar PayPal. Recarga la página.');
      setLoading(false);
    });

    document.body.appendChild(script);

    return () => {
      clearTimeout(timeoutId);
    };
  }, [accessType, authData, plan, navigate]);

  // ══════════════════════════════════════════════════════════════════
  // RENDERIZAR BOTONES PAYPAL
  // ══════════════════════════════════════════════════════════════════
  useEffect(() => {
    if (!sdkReady) return;
    if (!window.paypal) return;
    if (!plan || plan.isTrial) return;
    if (!paypalRef.current) return;

    console.log('🔵 Renderizando botones PayPal...');
    
    paypalRef.current.innerHTML = '';

    try {
      window.paypal.Buttons({
        style: {
          layout: 'vertical',
          color: 'gold',
          shape: 'rect',
          label: 'paypal',
          height: 45
        },

        createOrder: (data, actions) => {
          const amount = parseFloat(plan.finalPrice || plan.price || 0).toFixed(2);
          console.log('💰 Creando orden PayPal - Monto: $', amount);

          return actions.order.create({
            purchase_units: [{
              description: `${plan.name} - UrbanQ Condominio`,
              amount: {
                currency_code: 'USD',
                value: amount
              }
            }]
          });
        },

        onApprove: async (data, actions) => {
          console.log('✅ Pago aprobado - Order ID:', data.orderID);
          setProcessing(true);
          
          try {
            const order = await actions.order.capture();
            console.log('✅ Orden capturada:', order);
            await handlePaymentSuccess(order);
          } catch (err) {
            console.error('❌ Error capturando orden:', err);
            setError('Error al procesar el pago');
            setProcessing(false);
          }
        },

        onError: (err) => {
          console.error('❌ Error PayPal:', err);
          setError('Error con PayPal. Intenta nuevamente.');
          setProcessing(false);
        },

        onCancel: () => {
          console.log('⚠️ Pago cancelado por el usuario');
          setError(null);
          setProcessing(false);
        }

      }).render(paypalRef.current);

      console.log('✅ Botones PayPal renderizados exitosamente');
    } catch (error) {
      console.error('❌ Error renderizando PayPal:', error);
      setError('Error al cargar botones de pago');
    }
  }, [sdkReady, plan]);

  // ══════════════════════════════════════════════════════════════════
  // 🔥 ACTIVAR TRIAL - SOPORTA AMBOS FLUJOS
  // ══════════════════════════════════════════════════════════════════
  const handleTrialActivation = async () => {
    console.log('═══════════════════════════════════════════════');
    console.log('🎁 Activando TRIAL...');
    console.log('📋 Tipo de acceso:', accessType);
    console.log('═══════════════════════════════════════════════');
    
    setProcessing(true);
    setError(null);

    // API endpoints importados desde config/api.js
    const planType = getPlanType(plan.id);

    // ─────────────────────────────────────────────────────────────────
    // FLUJO 1: NUEVO REGISTRO (usa registrationToken)
    // ─────────────────────────────────────────────────────────────────
    if (accessType === 'registration') {
      console.log('🔵 Activando trial para NUEVO REGISTRO');
      
      if (!authData.registrationToken) {
        setError('Sesión de registro expirada. Por favor regístrate nuevamente.');
        setTimeout(() => navigate('/register'), 2000);
        return;
      }

      const requestData = {
        userId: parseInt(authData.userId),
        condominiumId: parseInt(authData.condominiumId),
        registrationToken: authData.registrationToken,
        planType: planType,
        isTrial: true,
        trialDays: plan.trialDays || 7,
        amount: 0.00,
        paypalOrderId: null,
        paypalTransactionId: null,
        discountCode: null
      };

      console.log('📤 Datos a enviar (registro):', requestData);

      try {
        // SIN Authorization header (no hay JWT aún)
        const response = await fetch(API_ENDPOINTS.subscriptionsCreate, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(requestData)
        });

        const data = await response.json();
        console.log('📥 Response:', data);

        if (!response.ok) {
          throw new Error(data.message || 'Error activando trial');
        }

        // Guardar token y usuario en localStorage
        if (data.success && data.token) {
          localStorage.setItem('token', data.token);
          localStorage.setItem('user', JSON.stringify(data.user));
          
          setSuccess(true);
          setTimeout(() => {
            navigate('/dashboard', {
              state: { welcome: true, isTrial: true, subscription: data.subscription }
            });
          }, 2000);
        } else {
          throw new Error(data.message || 'Error activando trial');
        }
      } catch (err) {
        console.error('❌ Error:', err);
        setError(err.message);
        setProcessing(false);
      }
      return;
    }

    // ─────────────────────────────────────────────────────────────────
    // FLUJO 2: ADMIN LOGUEADO (usa JWT token)
    // ✅ FIX v1.8.9.4: Agregado userId
    // ─────────────────────────────────────────────────────────────────
    if (accessType === 'logged_in') {
      console.log('🔵 Activando trial para ADMIN LOGUEADO');

      const requestData = {
        userId: parseInt(authData.userId),  // ✅ FIX: Agregado userId
        condominiumId: parseInt(authData.condominiumId),
        planType: planType,
        isTrial: true,
        trialDays: plan.trialDays || 7,
        amount: 0.00,
        paypalOrderId: null,
        paypalTransactionId: null,
        discountCode: plan.discount?.code || null
      };

      console.log('📤 Datos a enviar (logged_in):', requestData);

      try {
        // CON Authorization header (tiene JWT)
        const response = await fetch(API_ENDPOINTS.subscriptionsCreate, {
          method: 'POST',
          headers: { 
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${authData.jwtToken}`
          },
          body: JSON.stringify(requestData)
        });

        const data = await response.json();
        console.log('📥 Response:', data);

        if (!response.ok) {
          throw new Error(data.message || 'Error activando trial');
        }

        // Actualizar usuario en localStorage si viene actualizado
        if (data.user) {
          localStorage.setItem('user', JSON.stringify(data.user));
        }
        
        setSuccess(true);
        setTimeout(() => {
          navigate('/dashboard', {
            state: { welcome: true, isTrial: true, subscription: data.subscription }
          });
        }, 2000);

      } catch (err) {
        console.error('❌ Error:', err);
        setError(err.message);
        setProcessing(false);
      }
    }
  };

  // ══════════════════════════════════════════════════════════════════
  // 🔥 MANEJAR PAGO EXITOSO CON PAYPAL - SOPORTA AMBOS FLUJOS
  // ══════════════════════════════════════════════════════════════════
  const handlePaymentSuccess = async (paypalOrder) => {
    console.log('💳 Procesando pago exitoso...');
    console.log('📋 Tipo de acceso:', accessType);

    // API endpoints importados desde config/api.js
    const planType = getPlanType(plan.id);
    const amount = parseFloat(plan.finalPrice || plan.price || 0).toFixed(2);

    // Datos base para ambos flujos
    const baseData = {
      condominiumId: parseInt(authData.condominiumId),
      planType: planType,
      isTrial: false,
      trialDays: 0,
      amount: parseFloat(amount),
      paypalOrderId: paypalOrder.id,
      paypalTransactionId: paypalOrder.purchase_units?.[0]?.payments?.captures?.[0]?.id,
      discountCode: plan.discount?.code || null
    };

    try {
      let response;

      // ─────────────────────────────────────────────────────────────────
      // FLUJO 1: NUEVO REGISTRO
      // ─────────────────────────────────────────────────────────────────
      if (accessType === 'registration') {
        const requestData = {
          ...baseData,
          userId: parseInt(authData.userId),
          registrationToken: authData.registrationToken
        };

        console.log('📤 Datos a enviar (registro):', requestData);

        response = await fetch(API_ENDPOINTS.subscriptionsCreate, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(requestData)
        });
      }

      // ─────────────────────────────────────────────────────────────────
      // FLUJO 2: ADMIN LOGUEADO
      // ✅ FIX v1.8.9.4: Agregado userId
      // ─────────────────────────────────────────────────────────────────
      if (accessType === 'logged_in') {
        const requestData = {
          ...baseData,
          userId: parseInt(authData.userId)  // ✅ FIX: Agregado userId
        };

        console.log('📤 Datos a enviar (logged_in):', requestData);

        response = await fetch(API_ENDPOINTS.subscriptionsCreate, {
          method: 'POST',
          headers: { 
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${authData.jwtToken}`
          },
          body: JSON.stringify(requestData)
        });
      }

      const data = await response.json();
      console.log('📥 Response:', data);

      if (!response.ok) {
        throw new Error(data.message || 'Error procesando pago');
      }

      // Guardar/actualizar datos en localStorage
      if (data.token) {
        localStorage.setItem('token', data.token);
      }
      if (data.user) {
        localStorage.setItem('user', JSON.stringify(data.user));
      }

      setSuccess(true);
      setProcessing(false);
      
      setTimeout(() => {
        navigate('/dashboard', {
          state: { 
            welcome: true, 
            paymentSuccess: true,
            subscription: data.subscription 
          }
        });
      }, 2000);

    } catch (err) {
      console.error('❌ Error:', err);
      setError(err.message || 'Error al procesar el pago');
      setProcessing(false);
    }
  };

  // ══════════════════════════════════════════════════════════════════
  // RENDER - Loading state
  // ══════════════════════════════════════════════════════════════════
  if (accessType === 'loading') {
    return (
      <div className="checkout-page">
        <div className="checkout-container">
          <div className="loading-state">
            <div className="spinner"></div>
            <p>Verificando acceso...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!plan) {
    return (
      <div className="checkout-page">
        <div className="checkout-container">
          <div className="error-state">
            <h2>⚠️ No hay plan seleccionado</h2>
            <p>Redirigiendo a planes...</p>
          </div>
        </div>
      </div>
    );
  }

  // ══════════════════════════════════════════════════════════════════
  // RENDER - Checkout UI
  // ══════════════════════════════════════════════════════════════════
  return (
    <div className="checkout-page">
      <div className="checkout-container">
        {/* Header */}
        <div className="checkout-header">
          <button 
            className="back-btn"
            onClick={() => navigate('/planes')}
          >
            ← Volver a planes
          </button>
          <h1>Finalizar Suscripción</h1>
        </div>

        <div className="checkout-content">
          {/* ═══ COLUMNA IZQUIERDA: RESUMEN DEL PLAN ═══ */}
          <div className="plan-summary">
            <h2>Resumen de tu plan</h2>
            
            <div className="summary-card">
              {/* Header del plan */}
              <div className="summary-header">
                <div className="plan-icon">
                  {plan.isTrial ? '🎁' : '⭐'}
                </div>
                <div className="plan-name-badge">
                  <h3>{plan.name}</h3>
                  {plan.isTrial && (
                    <span className="trial-badge">
                      {plan.trialDays} días GRATIS
                    </span>
                  )}
                </div>
              </div>

              {/* Detalles del plan */}
              <div className="summary-details">
                <div className="detail-row">
                  <span className="detail-label">Duración</span>
                  <span className="detail-value">{plan.duration}</span>
                </div>
                
                <div className="detail-row">
                  <span className="detail-label">Precio por mes</span>
                  <span className="detail-value">${plan.pricePerMonth}/mes</span>
                </div>

                {plan.discount && (
                  <div className="detail-row discount">
                    <span className="detail-label">
                      Descuento <span className="discount-code">{plan.discount.code}</span>
                    </span>
                    <span className="detail-value">-{plan.discount.percentage}%</span>
                  </div>
                )}

                <div className="detail-row-total">
                  <span className="total-label">TOTAL A PAGAR {plan.isTrial && 'HOY'}</span>
                  <span className="total-amount">
                    ${plan.isTrial ? '0.00' : parseFloat(plan.finalPrice || plan.price).toFixed(2)}
                  </span>
                </div>
              </div>

              {/* Info de trial */}
              {plan.isTrial && (
                <div className="trial-info-box">
                  <div className="trial-info-item">
                    <span className="icon">✅</span>
                    <span>Tu prueba comienza hoy</span>
                  </div>
                  <div className="trial-info-item">
                    <span className="icon">📅</span>
                    <span>Termina el {new Date(Date.now() + plan.trialDays * 24 * 60 * 60 * 1000).toLocaleDateString('es-ES')}</span>
                  </div>
                  <div className="trial-info-item">
                    <span className="icon">💳</span>
                    <span>Sin cargos ni tarjeta requerida</span>
                  </div>
                </div>
              )}

              {/* Características incluidas */}
              {plan.features && (
                <div className="features-box">
                  <h4>✨ Características incluidas</h4>
                  <ul className="features-list">
                    {plan.features.slice(0, 6).map((feature, index) => (
                      <li key={index}>
                        <svg className="check-icon" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                        {feature}
                      </li>
                    ))}
                    {plan.features.length > 6 && (
                      <li className="more-features">
                        + {plan.features.length - 6} características más
                      </li>
                    )}
                  </ul>
                </div>
              )}
            </div>
          </div>

          {/* ═══ COLUMNA DERECHA: MÉTODO DE PAGO ═══ */}
          <div className="payment-section">
            <h2>{plan.isTrial ? '🎁 Activar Prueba Gratis' : '💳 Método de pago'}</h2>

            {/* Alertas */}
            {error && (
              <div className="alert alert-error">
                <strong>❌ Error</strong>
                <p>{error}</p>
              </div>
            )}

            {success && (
              <div className="alert alert-success">
                <div className="success-icon">✓</div>
                <h3>¡Suscripción activada con éxito!</h3>
                <p>Redirigiendo a tu dashboard...</p>
              </div>
            )}

            {!success && (
              <>
                {plan.isTrial ? (
                  /* ═══ ACTIVACIÓN DE TRIAL ═══ */
                  <div className="trial-activation">
                    <div className="trial-message">
                      <div className="trial-icon-big">🎉</div>
                      <h3>¡Todo listo para comenzar!</h3>
                      <p>
                        Activa tu prueba gratuita de <strong>{plan.trialDays} días</strong> ahora mismo.
                        Sin tarjeta de crédito, sin compromisos.
                      </p>
                    </div>

                    <div className="trial-benefits">
                      <div className="benefit-item">
                        <span className="benefit-icon">✅</span>
                        <span>Acceso completo a todas las funciones</span>
                      </div>
                      <div className="benefit-item">
                        <span className="benefit-icon">🚫</span>
                        <span>Sin cargos durante el período de prueba</span>
                      </div>
                      <div className="benefit-item">
                        <span className="benefit-icon">🔄</span>
                        <span>Cancela cuando quieras, sin penalización</span>
                      </div>
                    </div>

                    <button 
                      className="activate-trial-btn"
                      onClick={handleTrialActivation}
                      disabled={processing}
                    >
                      {processing ? (
                        <>
                          <span className="spinner-small"></span>
                          Activando...
                        </>
                      ) : (
                        <>
                          <span>✨</span>
                          Activar Prueba Gratis de {plan.trialDays} Días
                        </>
                      )}
                    </button>
                  </div>
                ) : (
                  /* ═══ PAGO CON PAYPAL ═══ */
                  <div className="payment-form">
                    {loading ? (
                      <div className="loading-state">
                        <div className="spinner"></div>
                        <p>Cargando métodos de pago seguros...</p>
                      </div>
                    ) : (
                      <div className="paypal-container">
                        <p className="payment-hint">
                          🔒 Pago 100% seguro procesado por PayPal
                        </p>
                        <div ref={paypalRef} className="paypal-buttons"></div>
                        {processing && (
                          <div className="processing-overlay">
                            <div className="spinner"></div>
                            <p>Procesando tu pago de forma segura...</p>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {/* Security badges */}
                <div className="security-section">
                  <div className="security-badges">
                    <div className="badge">🔒 SSL Seguro</div>
                    <div className="badge">✅ PCI DSS</div>
                    <div className="badge">🛡️ Datos Protegidos</div>
                  </div>

                  <div className="guarantee-box">
                    <h4>💯 Garantía de Satisfacción</h4>
                    <p>
                      Si no estás completamente satisfecho, cancela en cualquier 
                      momento durante los primeros 30 días y te reembolsaremos 
                      el 100% de tu dinero. Sin preguntas.
                    </p>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="checkout-footer">
          <p>
            ¿Necesitas ayuda? Contáctanos: 
            <a href="mailto:soporte@urbanq.com"> soporte@urbanq.com</a>
          </p>
        </div>
      </div>
    </div>
  );
}